import { X, Mail, Phone, MapPin, Calendar, Clock, Users, Briefcase, Car, Star, Moon, Repeat, CreditCard, FileText, User as UserIcon, History, Plane } from "lucide-react";
import { STATUS_COLORS, STATUS_LABELS } from "./shared";

/**
 * Read-only booking detail view. Shows EVERY field stored on the quote in a
 * clean two-column layout. Trigger this from the Bookings list "View details"
 * (eye) icon.
 */
export default function BookingDetailsModal({ quote, onClose, onModify }) {
  if (!quote) return null;
  const isVeniceNear = (label) => label === "Venice - Near Venice";
  const flight = quote.flight_details || null;

  const Row = ({ label, value, mono = false, testId }) => {
    if (value === undefined || value === null || value === "" || value === false) return null;
    return (
      <div className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5" data-testid={testId}>
        <span className="text-white/50 text-xs uppercase tracking-wide shrink-0 pt-0.5">{label}</span>
        <span className={`text-white text-sm text-right ${mono ? "font-mono" : ""} break-all`}>{value}</span>
      </div>
    );
  };

  const Section = ({ title, icon: Icon, children, testId }) => (
    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4" data-testid={testId}>
      <h4 className="text-[#C5A059] text-xs uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </h4>
      <div>{children}</div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="booking-details-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#111] border border-white/10 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#111] z-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-white">Booking Details</h3>
              <span className={`px-2 py-1 rounded text-[10px] uppercase border ${STATUS_COLORS[quote.status] || "border-white/20"}`}>
                {STATUS_LABELS[quote.status] || quote.status}
              </span>
              {quote.paid && (
                <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded flex items-center gap-1">
                  <CreditCard className="w-3 h-3" /> Paid
                </span>
              )}
              {quote.refunded && (
                <span className="text-[10px] bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded">Refunded</span>
              )}
            </div>
            <p className="text-[#C5A059] text-xs font-mono mt-1" data-testid="details-quote-id">{quote.quote_id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="details-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer */}
          <Section title="Customer" icon={UserIcon} testId="section-customer">
            <Row label="Name" value={quote.user_name} testId="row-user-name" />
            <Row
              label="Email"
              value={
                <a href={`mailto:${quote.user_email}`} className="text-[#C5A059] hover:underline inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {quote.user_email}
                </a>
              }
              testId="row-user-email"
            />
            <Row
              label="Phone"
              value={
                <a href={`tel:${quote.user_phone}`} className="text-[#C5A059] hover:underline inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {quote.user_phone}
                </a>
              }
              testId="row-user-phone"
            />
          </Section>

          {/* Trip */}
          <Section title="Trip" icon={MapPin} testId="section-trip">
            <Row label="From" value={quote.origin} testId="row-origin" />
            {quote.origin_specific_address && (
              <Row
                label={isVeniceNear(quote.origin) ? "Near Venice" : "Custom address"}
                value={<span className="text-amber-300">{quote.origin_specific_address}</span>}
                testId="row-origin-specific"
              />
            )}
            <Row label="To" value={quote.destination} testId="row-destination" />
            {quote.destination_specific_address && (
              <Row
                label={isVeniceNear(quote.destination) ? "Near Venice" : "Custom address"}
                value={<span className="text-amber-300">{quote.destination_specific_address}</span>}
                testId="row-dest-specific"
              />
            )}
            <Row label="Distance" value={quote.distance_km ? `${quote.distance_km} km` : null} />
            <Row label="Route ID" value={quote.route_id} mono />
            {quote.is_custom_route && <Row label="Type" value="Custom route" />}
          </Section>

          {/* Schedule */}
          <Section title="Schedule" icon={Calendar} testId="section-schedule">
            <Row
              label="Pickup"
              value={
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-[#C5A059]" /> {quote.pickup_date}
                  <Clock className="w-3 h-3 text-[#C5A059] ml-2" /> {quote.pickup_time}
                  {quote.is_night_service && <Moon className="w-3 h-3 text-[#C5A059] ml-1" title="Night service" />}
                </span>
              }
              testId="row-pickup"
            />
            {quote.is_round_trip && (
              <>
                <Row
                  label={<span className="inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> Return</span>}
                  value={quote.return_date && quote.return_time ? `${quote.return_date} at ${quote.return_time}` : "—"}
                  testId="row-return"
                />
              </>
            )}
            <Row label="Created" value={quote.created_at?.split(".")[0]?.replace("T", " ")} mono />
            <Row label="Updated" value={quote.updated_at?.split(".")[0]?.replace("T", " ")} mono />
          </Section>

          {/* Capacity & vehicle */}
          <Section title="Capacity & Vehicle" icon={Car} testId="section-capacity">
            <Row
              label="Passengers"
              value={
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3 text-[#C5A059]" /> {quote.passengers || 1}
                </span>
              }
              testId="row-pax"
            />
            <Row
              label="Luggage"
              value={
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="w-3 h-3 text-[#C5A059]" /> {quote.luggage ?? 0}
                </span>
              }
              testId="row-luggage"
            />
            <Row
              label="Vehicle"
              value={
                quote.vehicle_title ? (
                  <span className="inline-flex items-center gap-1">
                    <Car className="w-3 h-3 text-purple-300" /> {quote.vehicle_title}
                    {quote.vehicle_autoassigned && <span className="text-[10px] text-white/40 ml-1">(auto)</span>}
                  </span>
                ) : (
                  <span className="text-white/30">Not assigned</span>
                )
              }
              testId="row-vehicle"
            />
            <Row
              label="Driver"
              value={
                quote.driver_name ? (
                  <span className="inline-flex items-center gap-1 text-violet-300">{quote.driver_name}</span>
                ) : (
                  <span className="text-white/30">Not assigned</span>
                )
              }
              testId="row-driver"
            />
            {quote.driver_phone && (
              <Row
                label="Driver phone"
                value={
                  <a href={`tel:${quote.driver_phone}`} className="text-[#C5A059] hover:underline">
                    {quote.driver_phone}
                  </a>
                }
              />
            )}
            {quote.driver_assigned_at && (
              <Row label="Driver assigned" value={quote.driver_assigned_at.split(".")[0]?.replace("T", " ")} mono />
            )}
          </Section>

          {/* Premium Enhancements */}
          {quote.luxury_options_snapshot && quote.luxury_options_snapshot.length > 0 && (
            <Section title="Premium Enhancements" icon={Star} testId="section-premium">
              <div className="space-y-1.5">
                {quote.luxury_options_snapshot.map((lb) => (
                  <div
                    key={lb.luxury_option_id}
                    className="flex items-center justify-between p-2 bg-[#1F1F1F] border border-[#C5A059]/15 rounded-sm"
                    data-testid={`premium-line-${lb.luxury_option_id}`}
                  >
                    <span className="text-white text-sm flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 text-[#C5A059]" /> {lb.title}
                      <span className="text-[10px] text-[#C5A059]/80 ml-1">+{lb.surcharge_pct}%</span>
                    </span>
                    <span className="text-[#C5A059] font-mono text-xs">+€{Number(lb.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Flight details */}
          {flight && (flight.flight_number || flight.airline || flight.arrival_time) && (
            <Section title="Flight details" icon={Plane} testId="section-flight">
              <Row label="Airline" value={flight.airline} />
              <Row label="Flight #" value={flight.flight_number} mono />
              <Row label="Arrival from" value={flight.origin_airport} />
              <Row label="Arrival time" value={flight.arrival_time} />
              <Row label="Terminal" value={flight.terminal} />
            </Section>
          )}

          {/* Pricing breakdown */}
          <Section title="Pricing" icon={CreditCard} testId="section-pricing">
            <Row label="Base price" value={`€${Number(quote.base_price || 0).toFixed(2)}`} mono />
            {quote.passenger_surcharge > 0 && (
              <Row
                label={`Group surcharge${quote.passenger_bracket_label ? ` (${quote.passenger_bracket_label})` : ""}`}
                value={<span className="text-[#C5A059] font-mono">+€{Number(quote.passenger_surcharge).toFixed(2)}</span>}
              />
            )}
            {quote.luxury_surcharge > 0 && (
              <Row
                label="Premium surcharges total"
                value={<span className="text-[#C5A059] font-mono">+€{Number(quote.luxury_surcharge).toFixed(2)}</span>}
              />
            )}
            {quote.night_surcharge > 0 && (
              <Row
                label="Night surcharge"
                value={<span className="text-[#C5A059] font-mono">+€{Number(quote.night_surcharge).toFixed(2)}</span>}
              />
            )}
            {quote.round_trip_discount > 0 && (
              <Row
                label="Round-trip discount"
                value={<span className="text-emerald-400 font-mono">-€{Number(quote.round_trip_discount).toFixed(2)}</span>}
              />
            )}
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-[#C5A059]/30">
              <span className="text-white font-semibold">Total</span>
              <span className="text-2xl font-bold text-[#C5A059] font-mono" data-testid="details-total">
                €{Number(quote.total_price || 0).toFixed(2)}
              </span>
            </div>
          </Section>

          {/* Notes / payment / refund */}
          {(quote.notes || quote.payment_intent_id || quote.refunded_at) && (
            <Section title="Operations" icon={FileText} testId="section-ops">
              <Row label="Internal notes" value={quote.notes} />
              <Row label="Payment intent" value={quote.payment_intent_id} mono />
              <Row label="Paid at" value={quote.paid_at?.split(".")[0]?.replace("T", " ")} mono />
              <Row label="Refunded at" value={quote.refunded_at?.split(".")[0]?.replace("T", " ")} mono />
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/5 p-4 flex items-center justify-between bg-[#0E0E0E] sticky bottom-0">
          <span className="text-white/40 text-xs inline-flex items-center gap-1">
            <History className="w-3 h-3" /> Use the History icon in the list for the full status timeline.
          </span>
          <div className="flex items-center gap-2">
            {onModify && (
              <button
                onClick={() => { onModify(quote); onClose(); }}
                className="px-4 py-2 text-sm bg-violet-500/15 border border-violet-500/40 text-violet-300 hover:bg-violet-500/25 rounded-sm"
                data-testid="details-modify-btn"
              >
                Modify booking…
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-sm"
              data-testid="details-close-footer-btn"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
